import { api } from "@/lib/api/client";

/** A single delivery channel (email / push / slack / …) for a notification
 *  category, plus whether the member can currently use it. `available` is
 *  false when a prerequisite is missing (no phone number, Slack not linked,
 *  browser notifications not enabled); the UI disables the control and shows
 *  a hint in that case. `enabled` is the member's current opt-in state. */
export interface CommsChannel {
  key: string;
  label: string;
  available: boolean;
  enabled: boolean;
}

/** One row of the preference matrix — a notification category (e.g.
 *  "Doorbell", "Announcements") and the channels it can be delivered on. */
export interface CommsPreference {
  category: string;
  label: string;
  description: string;
  channels: CommsChannel[];
  /** False when a shared destination (target) can't take this category — the
   *  backend refuses it on targets (e.g. `doorbell_call`, which rings people).
   *  Absent = targetable. */
  targetable?: boolean;
  /** Destination scopes that may take it; the doorbell is never on a site destination.
   *  Absent = every scope. */
  target_scopes?: string[];
}

/** Web-push subscription payload, matching the browser's
 *  ``PushSubscription.toJSON()`` shape that the backend persists. */
export interface PushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/** Which shared inbox a target belongs to. `tenant` targets are managed by
 *  tenant admins, `site` targets by site admins — the backend derives the
 *  actual tenant/site from the caller's profile, so no id is ever sent. */
export type CommsTargetScope = "tenant" | "site";

/** The four address-carrying channels a target can use. Identity channels
 *  (browser push, portal inbox, Slack DM) stay preference-only — they have no
 *  address an admin could type. */
export type CommsTargetChannel = "email" | "sms" | "slack_channel" | "slack_webhook";

/** A notification destination the whole tenant or site notifies — a shared
 *  reception inbox, an on-call SMS number, a Slack channel, a webhook. Unlike
 *  a `CommsPreference` (which channels reach *me*), a target is an explicit
 *  address that fires for everyone in the scope. */
export interface CommsTarget {
  id: number;
  scope: CommsTargetScope;
  channel: CommsTargetChannel;
  /** Email address, E.164 phone number, Slack channel id, or webhook URL —
   *  normalized by the backend on write. */
  address: string;
  /** Category keys (see `CommsPreference.category`) this target fires for.
   *  Never empty — the backend rejects an empty list. */
  categories: string[];
  label: string;
  enabled: boolean;
}

/** Body for creating a target. */
export interface CommsTargetCreate {
  scope: CommsTargetScope;
  channel: CommsTargetChannel;
  address: string;
  categories: string[];
  label?: string;
  enabled?: boolean;
}

/** Body for updating a target — every field is optional (PATCH semantics).
 *  Scope and channel are immutable; delete and re-create to change them. */
export interface CommsTargetUpdate {
  address?: string;
  categories?: string[];
  label?: string;
  enabled?: boolean;
}

export const commsApi = {
  /** Full category × channel matrix for the signed-in member. */
  getPreferences: () => api.get<CommsPreference[]>("/comms/preferences/"),

  /** Replace the enabled channels for a single category. The body is the
   *  complete desired set of channel keys — the backend treats it as a
   *  whole-row replace, not a delta. */
  updatePreference: (category: string, channels: string[]) =>
    api.put<CommsPreference>(`/comms/preferences/${category}/`, { channels }),

  /** VAPID application-server public key used to subscribe to web push. */
  getVapidKey: () => api.get<{ public_key: string }>("/comms/vapid-public-key/"),

  /** Persist a browser push subscription so the backend can deliver to it. */
  savePushSubscription: (sub: PushSubscriptionPayload) =>
    api.post("/comms/push-subscriptions/", sub),

  /** Forget a push subscription (on unsubscribe / sign-out). */
  deletePushSubscription: (endpoint: string) =>
    api.delete("/comms/push-subscriptions/", { data: { endpoint } }),

  /** Shared destinations for one scope. 403 when the member doesn't administer
   *  that scope — callers gate on `is_tenant_admin` / `is_site_admin` first. */
  getTargets: (scope: CommsTargetScope) =>
    api.get<CommsTarget[]>("/comms/targets/", { params: { scope } }),

  createTarget: (body: CommsTargetCreate) => api.post<CommsTarget>("/comms/targets/", body),

  updateTarget: (id: number, body: CommsTargetUpdate) =>
    api.patch<CommsTarget>(`/comms/targets/${id}/`, body),

  deleteTarget: (id: number) => api.delete(`/comms/targets/${id}/`),
};
